import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Briefcase, Building2, CheckCircle, ChevronDown, Clock, FileText, Gift, History, LayoutDashboard, List, LogOut, Menu, Plus, Settings, User, X } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const Sidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [expandedSections, setExpandedSections] = useState(['companies', 'jobs']);

  const toggleSection = (section) => {
    if (collapsed) return;
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div
      className={`main-sidebar transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'} ${mobileOpen ? 'mobile-open' : ''}`}
      data-testid="main-sidebar"
    >
      <div className="h-16 px-4 border-b border-border flex items-center justify-between">
        {collapsed
          ? <img src="/logo-icon.png" alt="DR" className="w-8 h-8 object-contain mx-auto" />
          : <img src="/logo-full.png" alt="Digital Reporting" className="h-8 w-auto" />
        }
        {/* Close button on mobile, toggle on desktop */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (mobileOpen) { setMobileOpen(false); }
            else { setCollapsed(!collapsed); }
          }}
          className={`${collapsed && !mobileOpen ? 'mx-auto' : 'ml-auto'} hidden md:inline-flex`}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobileOpen(false)}
          className="ml-auto md:hidden"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      <nav className="sidebar-menu overflow-y-auto">
        <div className="sidebar-section">
          {!collapsed && <div className="sidebar-section-title">Main Menu</div>}

          <button
            className={`sidebar-item w-full text-left ${isActive('/') ? 'active' : ''} ${collapsed ? 'justify-center' : ''} ${isActive('/') && collapsed ? 'collapsed-active' : ''}`}
            onClick={() => navigate('/')}
            data-testid="nav-dashboard"
            title={collapsed ? 'Dashboard' : undefined}
            aria-label="Dashboard"
          >
            <LayoutDashboard className={collapsed ? 'w-6 h-6' : 'w-4 h-4'} />
            {!collapsed && <span>Dashboard</span>}
          </button>
        </div>

        <div className="sidebar-section">
          <button
            className={`sidebar-item w-full text-left ${collapsed ? 'justify-center' : ''}`}
            onClick={() => collapsed ? navigate('/companies') : toggleSection('companies')}
            title={collapsed ? 'My Companies' : undefined}
            aria-label="My Companies"
            aria-expanded={!collapsed ? expandedSections.includes('companies') : undefined}
          >
            <Building2 className={collapsed ? 'w-6 h-6' : 'w-4 h-4'} />
            {!collapsed && (
              <>
                <span className="flex-1">My Companies</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.includes('companies') ? 'rotate-180' : ''}`} />
              </>
            )}
          </button>

          {!collapsed && expandedSections.includes('companies') && (
            <div className="sidebar-submenu" role="group" aria-label="Companies submenu">
              <button
                className={`sidebar-item w-full text-left ${isActive('/companies') ? 'active' : ''}`}
                onClick={() => navigate('/companies')}
                data-testid="nav-companies-list"
              >
                <List className="w-4 h-4" />
                <span>All Companies</span>
              </button>
              <button
                className="sidebar-item w-full text-left"
                onClick={() => navigate('/companies?add=1')}
                data-testid="nav-companies-add"
              >
                <Plus className="w-4 h-4" />
                <span>Add Company</span>
              </button>
            </div>
          )}
        </div>

        <div className="sidebar-section">
          <button
            className={`sidebar-item w-full text-left ${collapsed ? 'justify-center' : ''}`}
            onClick={() => collapsed ? navigate('/jobs') : toggleSection('jobs')}
            title={collapsed ? 'Jobs' : undefined}
            aria-label="Jobs"
            aria-expanded={!collapsed ? expandedSections.includes('jobs') : undefined}
          >
            <Briefcase className={collapsed ? 'w-6 h-6' : 'w-4 h-4'} />
            {!collapsed && (
              <>
                <span className="flex-1">Jobs</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.includes('jobs') ? 'rotate-180' : ''}`} />
              </>
            )}
          </button>

          {!collapsed && expandedSections.includes('jobs') && (
            <div className="sidebar-submenu" role="group" aria-label="Jobs submenu">
              <button
                className={`sidebar-item w-full text-left ${isActive('/jobs') ? 'active' : ''}`}
                onClick={() => navigate('/jobs')}
                data-testid="nav-jobs-all"
              >
                <List className="w-4 h-4" />
                <span>All Jobs</span>
              </button>
              <button
                className="sidebar-item w-full text-left"
                onClick={() => navigate('/jobs?status=active')}
                data-testid="nav-jobs-pending"
              >
                <Clock className="w-4 h-4" />
                <span>In Progress</span>
              </button>
              <button
                className="sidebar-item w-full text-left"
                onClick={() => navigate('/jobs?status=completed')}
                data-testid="nav-jobs-completed"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Completed</span>
              </button>
            </div>
          )}
        </div>

        <div className="sidebar-section">
          {!collapsed && <div className="sidebar-section-title">Other</div>}

          <button
            className={`sidebar-item w-full text-left ${isActive('/history') ? 'active' : ''} ${collapsed ? 'justify-center' : ''} ${isActive('/history') && collapsed ? 'collapsed-active' : ''}`}
            onClick={() => navigate('/history')}
            data-testid="nav-history"
            title={collapsed ? 'Filing History' : undefined}
            aria-label="Filing History"
          >
            <History className={collapsed ? 'w-6 h-6' : 'w-4 h-4'} />
            {!collapsed && <span>Filing History</span>}
          </button>

          <button
            className={`sidebar-item w-full text-left ${isActive('/invoices') ? 'active' : ''} ${collapsed ? 'justify-center' : ''} ${isActive('/invoices') && collapsed ? 'collapsed-active' : ''}`}
            onClick={() => navigate('/invoices')}
            data-testid="nav-invoices"
            title={collapsed ? 'Invoices' : undefined}
            aria-label="Invoices"
          >
            <FileText className={collapsed ? 'w-6 h-6' : 'w-4 h-4'} />
            {!collapsed && <span>Invoices</span>}
          </button>

          <button
            className={`sidebar-item w-full text-left ${isActive('/referrals') ? 'active' : ''} ${collapsed ? 'justify-center' : ''} ${isActive('/referrals') && collapsed ? 'collapsed-active' : ''}`}
            onClick={() => navigate('/referrals')}
            data-testid="nav-referrals"
            title={collapsed ? 'Referrals' : undefined}
            aria-label="Referrals"
          >
            <Gift className={collapsed ? 'w-6 h-6' : 'w-4 h-4'} />
            {!collapsed && (
              <>
                <span>Referrals</span>
                <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">New</span>
              </>
            )}
          </button>

          <button
            className={`sidebar-item w-full text-left ${isActive('/settings') ? 'active' : ''} ${collapsed ? 'justify-center' : ''} ${isActive('/settings') && collapsed ? 'collapsed-active' : ''}`}
            onClick={() => navigate('/settings')}
            data-testid="nav-settings"
            title={collapsed ? 'Settings' : undefined}
            aria-label="Settings"
          >
            <Settings className={collapsed ? 'w-6 h-6' : 'w-4 h-4'} />
            {!collapsed && <span>Settings</span>}
          </button>
        </div>
      </nav>

      {/* User profile + sign out */}
      <div className={`border-t border-border p-3 flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
          {(user?.user_metadata?.full_name || user?.email || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || <User className="w-4 h-4" />}
        </div>
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{user?.user_metadata?.full_name || user?.email || 'User'}</p>
              {user?.user_metadata?.full_name && <p className="text-xs text-muted-foreground truncate">{user?.email}</p>}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 text-muted-foreground hover:text-destructive"
              title="Sign out"
              onClick={async () => { await signOut(); navigate('/login'); }}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
